import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import Sidebar from './Sidebar';
import { Link as RouterLink } from 'react-router-dom';

const DRAWER_WIDTH = 240;
const HEADER_HEIGHT = 64;

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Skip to main content link for screen readers */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 999999,
          padding: '8px 16px',
          backgroundColor: '#1976d2',
          color: '#ffffff',
          textDecoration: 'none',
          borderRadius: '4px',
          fontWeight: 600,
        }}
        onFocus={(e) => {
          e.target.style.left = '6px';
          e.target.style.top = '7px';
        }}
        onBlur={(e) => {
          e.target.style.left = '-9999px';
          e.target.style.top = 'auto';
        }}
      >
        Skip to main content
      </a>
      
      <AppBar
        position="fixed"
        component="header"
        role="banner"
        className="no-print"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#000000',
          '@media print': { display: 'none !important' },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="Open navigation menu"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon aria-hidden="true" />
          </IconButton>
          <RouterLink to="/dashboard" aria-label="Sony - Go to homepage" style={{ display: 'flex', alignItems: 'center', marginRight: '12px' }}>
            <img 
              src="/assets/images/sony-logo.svg" 
              alt="" 
              aria-hidden="true"
              style={{ height: '24px', width: 'auto' }}
            />
          </RouterLink>
          <Typography 
            variant="h6" 
            noWrap 
            component="span" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 300,
              letterSpacing: '-0.01em',
              fontSize: '1.25rem',
              color: '#ffffff'
            }}
            id="app-title"
          >
            Carson Quality Assurance Department
          </Typography>
          <IconButton
            id="user-profile-button"
            size="large"
            edge="end"
            onClick={handleProfileMenuOpen}
            color="inherit"
            aria-label="Open user menu"
            aria-controls={anchorEl ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl)}
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {(() => {
                const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
                return userData.username?.charAt(0) || user?.name?.charAt(0) || <AccountCircle />;
              })()} 
            </Avatar>
          </IconButton>
          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            MenuListProps={{
              'aria-labelledby': 'user-profile-button',
              role: 'menu',
            }}
          >
            <MenuItem onClick={handleProfileMenuClose} role="menuitem">
              <AccountCircle sx={{ mr: 1 }} aria-hidden="true" />
              {(() => {
                const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
                return userData.username || user?.name || 'Profile';
              })()}
            </MenuItem>
            <MenuItem onClick={handleLogout} role="menuitem">
              <Logout sx={{ mr: 1 }} aria-hidden="true" />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flex: 1, mt: `${HEADER_HEIGHT}px`, '@media print': { display: 'block', marginTop: '0 !important' } }}>
        <Box
          component="nav"
          aria-label="Main navigation"
          className="no-print"
          sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 }, '@media print': { display: 'none !important', width: '0 !important' } }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, top: `${HEADER_HEIGHT}px`, height: `calc(100% - ${HEADER_HEIGHT}px)` },
            }}
          >
            <Sidebar />
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: DRAWER_WIDTH,
                top: `${HEADER_HEIGHT}px`,
                height: `calc(100% - ${HEADER_HEIGHT}px)`,
                borderRight: 'none'
              },
            }}
            open
          >
            <Sidebar />
          </Drawer>
        </Box>

        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
            '@media print': { width: '100% !important', margin: '0 !important', padding: '0.5cm !important' },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired
};

export default Layout;